// ============================================================================
// PublicationCommentInput — saisie de commentaire du fil social (E1b)
// ============================================================================
// Sous-composant compagnon de PublicationCard (zone dépliée children) : avatar
// du viewer + champ texte 1 ligne (textarea auto-extensible non redimensionnable)
// + bouton d'envoi. Entrée soumet, Maj+Entrée insère un saut de ligne (saisie
// conversationnelle). Le champ se vide à la soumission — l'appelant porte la
// mutation OPTIMISTE (rollback = retrait de la liste + toast, pas de restauration
// du champ). AUCUNE autorisation ici : si le viewer ne peut pas commenter, le
// parent ne rend simplement pas la saisie (verdict serveur, bind-to-fetch).
// Data-driven, i18n par props (JAMAIS de t() dans @gs/ui) — placeholder et
// libellé d'envoi déjà traduits. Tokens slate-*/brand-* + échelle nommée
// uniquement (règle #28) — zéro hex.

import { useState, type KeyboardEvent } from 'react';
import { GsAvatar } from './GsAvatar';

export interface PublicationCommentInputProps {
  /** Nom du viewer (avatar initiales) — jamais déduit ici, fourni par l'appelant. */
  authorName: string;
  authorPhotoUrl?: string | null;
  /** Placeholder traduit (« Ajouter un commentaire… »). */
  placeholder: string;
  /** Libellé traduit du bouton d'envoi (« Commenter »). */
  submitLabel: string;
  /** Mutation en vol : champ + bouton inertes (pas de double-envoi). */
  pending?: boolean;
  /** Borne serveur (NVARCHAR(4000) — mig 144). */
  maxLength?: number;
  /** Reçoit le corps TRIMÉ, jamais vide (l'envoi à vide est inerte). */
  onSubmit: (body: string) => void;
  className?: string;
}

/** Saisie de commentaire — Entrée soumet, Maj+Entrée = saut de ligne. */
export function PublicationCommentInput({
  authorName,
  authorPhotoUrl,
  placeholder,
  submitLabel,
  pending = false,
  maxLength = 4000,
  onSubmit,
  className = '',
}: PublicationCommentInputProps) {
  const [value, setValue] = useState('');
  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && !pending;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
    setValue('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form
      className={`flex items-start gap-2.5 ${className}`.trim()}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <GsAvatar name={authorName} photoUrl={authorPhotoUrl} size="sm" />
      <textarea
        rows={1}
        value={value}
        disabled={pending}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        className="min-w-0 flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-body text-slate-700 placeholder:text-slate-400 focus:border-brand-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder:text-slate-500"
      />
      <button
        type="submit"
        disabled={!canSubmit}
        className="touch-manipulation rounded-lg bg-slate-100 px-4 py-2 text-field font-semibold text-slate-800 transition-colors hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
      >
        {submitLabel}
      </button>
    </form>
  );
}
